import { useEffect, useState } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Clock,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Save,
  X
} from 'lucide-react';
import { api } from '../../lib/api';
import { useUIStore } from '../../store/ui';
import { formatPrice, getTrackColorClasses } from '../../lib/utils';

interface TrackType {
  id: string;
  name: string;
  slug: string;
  color: string;
  description?: string;
  pricePerSlot: number;
  status: string;
  features: string[];
}

interface TimeSlot {
  id: string;
  name: string;
  trackTypeId: string;
  startTime: string;
  endTime: string;
  recurrenceType: string;
  daysOfWeek?: string;
  isActive: boolean;
}

export function AdminTracks() {
  const { addToast } = useUIStore();
  const [tracks, setTracks] = useState<TrackType[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedTrack, setExpandedTrack] = useState<string | null>(null);

  // Edit track modal
  const [editingTrack, setEditingTrack] = useState<TrackType | null>(null);
  const [trackForm, setTrackForm] = useState({
    name: '',
    description: '',
    pricePerSlot: 0,
    color: 'red',
    status: 'available',
  });
  const [saving, setSaving] = useState(false);

  // Add slot modal
  const [addingSlotForTrack, setAddingSlotForTrack] = useState<string | null>(null);
  const [slotForm, setSlotForm] = useState({
    name: '',
    startTime: '08:00',
    endTime: '12:00',
    recurrenceType: 'weekly',
    daysOfWeek: '1,2,3,4,5',
  });

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const [tracksRes, slotsRes] = await Promise.all([
        api.get('/api/admin/tracks'),
        api.get('/api/admin/time-slots'),
      ]);

      if (tracksRes.tracks) {
        setTracks(tracksRes.tracks);
      }
      if (slotsRes.timeSlots) {
        setTimeSlots(slotsRes.timeSlots);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }

  function openEditTrack(track: TrackType) {
    setEditingTrack(track);
    setTrackForm({
      name: track.name,
      description: track.description || '',
      pricePerSlot: track.pricePerSlot,
      color: track.color,
      status: track.status,
    });
  }

  async function handleSaveTrack() {
    if (!editingTrack) return;

    setSaving(true);
    try {
      await api.patch(`/api/admin/tracks/${editingTrack.id}`, trackForm);
      setTracks(
        tracks.map((t) =>
          t.id === editingTrack.id ? { ...t, ...trackForm } : t
        )
      );
      addToast('Track updated successfully', 'success');
      setEditingTrack(null);
    } catch (error) {
      addToast('Failed to update track', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddSlot() {
    if (!addingSlotForTrack) return;

    setSaving(true);
    try {
      const res = await api.post('/api/admin/time-slots', {
        ...slotForm,
        trackTypeId: addingSlotForTrack,
      });

      if (res.timeSlot) {
        setTimeSlots([...timeSlots, res.timeSlot]);
        addToast('Time slot added successfully', 'success');
        setAddingSlotForTrack(null);
        setSlotForm({
          name: '',
          startTime: '08:00',
          endTime: '12:00',
          recurrenceType: 'weekly',
          daysOfWeek: '1,2,3,4,5',
        });
      }
    } catch (error) {
      addToast('Failed to add time slot', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleSlot(slotId: string, isActive: boolean) {
    try {
      await api.patch(`/api/admin/time-slots/${slotId}`, { isActive: !isActive });
      setTimeSlots(
        timeSlots.map((s) => (s.id === slotId ? { ...s, isActive: !isActive } : s))
      );
      addToast(`Time slot ${!isActive ? 'enabled' : 'disabled'}`, 'success');
    } catch (error) {
      addToast('Failed to update time slot', 'error');
    }
  }

  async function handleDeleteSlot(slotId: string) {
    if (!confirm('Are you sure you want to delete this time slot?')) return;

    try {
      await api.delete(`/api/admin/time-slots/${slotId}`);
      setTimeSlots(timeSlots.filter((s) => s.id !== slotId));
      addToast('Time slot deleted', 'success');
    } catch (error) {
      addToast('Failed to delete time slot', 'error');
    }
  }

  const getDayNames = (daysStr?: string) => {
    if (!daysStr) return 'No days set';
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return daysStr.split(',').map((d) => dayNames[parseInt(d)]).join(', ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Track Settings</h1>
        <p className="text-gray-400 mt-1">
          Manage track types and time slots.
        </p>
      </div>

      {/* Tracks List */}
      <div className="space-y-4">
        {tracks.map((track) => {
          const colors = getTrackColorClasses(track.color);
          const trackSlots = timeSlots.filter((s) => s.trackTypeId === track.id);
          const isExpanded = expandedTrack === track.id;

          return (
            <div
              key={track.id}
              className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden"
            >
              {/* Track Header */}
              <div className={`h-1 ${colors.bg}`} />
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-lg ${colors.bg} flex items-center justify-center`}>
                      <span className="text-white font-bold text-lg">
                        {track.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{track.name}</h3>
                      <p className="text-gray-400 text-sm">{track.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="text-right">
                      <div className="text-lg font-bold text-white">
                        {formatPrice(track.pricePerSlot)}
                      </div>
                      <div className="text-sm text-gray-400">per slot</div>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        track.status === 'available'
                          ? 'bg-green-900/50 text-green-400'
                          : 'bg-red-900/50 text-red-400'
                      }`}
                    >
                      {track.status}
                    </span>
                    <button
                      onClick={() => openEditTrack(track)}
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <Edit2 size={18} className="text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* Expand/Collapse */}
                <button
                  onClick={() => setExpandedTrack(isExpanded ? null : track.id)}
                  className="mt-4 flex items-center text-sm text-gray-400 hover:text-gray-300"
                >
                  <Clock size={16} className="mr-2" />
                  {trackSlots.length} time slot{trackSlots.length !== 1 ? 's' : ''}
                  {isExpanded ? (
                    <ChevronUp size={16} className="ml-1" />
                  ) : (
                    <ChevronDown size={16} className="ml-1" />
                  )}
                </button>
              </div>

              {/* Time Slots (Expanded) */}
              {isExpanded && (
                <div className="border-t border-gray-700 p-4 bg-gray-750">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-white">Time Slots</h4>
                    <button
                      onClick={() => setAddingSlotForTrack(track.id)}
                      className="btn btn-sm bg-gray-700 text-white hover:bg-gray-600"
                    >
                      <Plus size={14} className="mr-1" />
                      Add Slot
                    </button>
                  </div>

                  {trackSlots.length === 0 ? (
                    <p className="text-gray-500 text-sm">
                      No time slots configured for this track.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {trackSlots.map((slot) => (
                        <div
                          key={slot.id}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            slot.isActive ? 'bg-gray-700' : 'bg-gray-700/50'
                          }`}
                        >
                          <div>
                            <div className={`font-medium ${slot.isActive ? 'text-white' : 'text-gray-500'}`}>
                              {slot.name}
                            </div>
                            <div className="text-sm text-gray-400">
                              {slot.startTime} - {slot.endTime} &bull; {getDayNames(slot.daysOfWeek)}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleToggleSlot(slot.id, slot.isActive)}
                              className={`px-3 py-1 text-xs rounded-full ${
                                slot.isActive
                                  ? 'bg-green-900/50 text-green-400'
                                  : 'bg-gray-600 text-gray-400'
                              }`}
                            >
                              {slot.isActive ? 'Active' : 'Inactive'}
                            </button>
                            <button
                              onClick={() => handleDeleteSlot(slot.id)}
                              className="p-1 hover:bg-gray-600 rounded transition-colors"
                            >
                              <Trash2 size={14} className="text-gray-400 hover:text-red-400" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit Track Modal */}
      {editingTrack && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl shadow-xl max-w-md w-full border border-gray-700">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Edit Track</h2>
              <button
                onClick={() => setEditingTrack(null)}
                className="p-1 hover:bg-gray-700 rounded"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={trackForm.name}
                  onChange={(e) => setTrackForm({ ...trackForm, name: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={trackForm.description}
                  onChange={(e) => setTrackForm({ ...trackForm, description: e.target.value })}
                  rows={3}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Price per Slot (cents)
                </label>
                <input
                  type="number"
                  value={trackForm.pricePerSlot}
                  onChange={(e) => setTrackForm({ ...trackForm, pricePerSlot: parseInt(e.target.value) })}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Display price: {formatPrice(trackForm.pricePerSlot)}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Color
                  </label>
                  <select
                    value={trackForm.color}
                    onChange={(e) => setTrackForm({ ...trackForm, color: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2"
                  >
                    <option value="red">Red</option>
                    <option value="blue">Blue</option>
                    <option value="green">Green</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={trackForm.status}
                    onChange={(e) => setTrackForm({ ...trackForm, status: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2"
                  >
                    <option value="available">Available</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="unavailable">Unavailable</option>
                  </select>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setEditingTrack(null)}
                  className="btn flex-1 bg-gray-700 text-white hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTrack}
                  disabled={saving}
                  className="btn flex-1 bg-red-600 text-white hover:bg-red-700"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Slot Modal */}
      {addingSlotForTrack && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl shadow-xl max-w-md w-full border border-gray-700">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Add Time Slot</h2>
              <button
                onClick={() => setAddingSlotForTrack(null)}
                className="p-1 hover:bg-gray-700 rounded"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Slot Name
                </label>
                <input
                  type="text"
                  value={slotForm.name}
                  onChange={(e) => setSlotForm({ ...slotForm, name: e.target.value })}
                  placeholder="e.g., Morning Session"
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={slotForm.startTime}
                    onChange={(e) => setSlotForm({ ...slotForm, startTime: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={slotForm.endTime}
                    onChange={(e) => setSlotForm({ ...slotForm, endTime: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Days of Week
                </label>
                <input
                  type="text"
                  value={slotForm.daysOfWeek}
                  onChange={(e) => setSlotForm({ ...slotForm, daysOfWeek: e.target.value })}
                  placeholder="0,1,2,3,4,5,6 (Sun-Sat)"
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2"
                />
                <p className="text-sm text-gray-500 mt-1">
                  0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setAddingSlotForTrack(null)}
                  className="btn flex-1 bg-gray-700 text-white hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSlot}
                  disabled={saving || !slotForm.name}
                  className="btn flex-1 bg-red-600 text-white hover:bg-red-700"
                >
                  {saving ? 'Adding...' : 'Add Slot'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
